/**
 * Responsibility: Implement admin access, role grants, and feature flag state for the UI layer.
 * Scope: Admin access/flag persistence only; screen rendering and route orchestration stay elsewhere.
 */
import { authStore } from './auth-store'
import { loadAdminConfig, saveAdminConfig } from "@anyquest/shared-core"
import {
  type AdminFeatureFlags,
  type AdminRoleGrant,
  type AdminUserRole,
  DEFAULT_ADMIN_FEATURE_FLAGS,
  E2E_ADMIN_KEY,
  LOCAL_ADMINS_KEY,
  LOCAL_FLAGS_KEY,
  SUPREME_ADMIN_EMAIL,
  buildLegacyAdminRoleGrants,
  hasE2EAdminOverride,
  isAdminRole,
  loadLocalLegacyAdmins,
  loadLocalRoleGrants,
  normalizeFeatureFlags,
  normalizeRoleGrantList,
  resolveAssignedRole,
  roleGrantKey,
  saveLocalRoleGrants,
  writeAdminRuntimeFlags,
} from '../../admin/admin-contract'
import {
  type CommercePlan,
  type CommerceTesterGrant,
  type PreforgedOffer,
  DEFAULT_COMMERCE_PLANS,
  normalizeCommerceTesterGrant,
  normalizeCommerceTesterGrants,
  normalizeCommercePlan,
  normalizeCommercePlans,
  normalizePreforgedOffer,
  normalizePreforgedOffers,
  readLocalCommerceTesterGrants,
  readLocalCommercePlans,
  readLocalPreforgedOffers,
  saveLocalCommerceTesterGrants,
  saveLocalCommercePlans,
  saveLocalPreforgedOffers,
  testerGrantKey,
} from '../../commerce/commerce-contract'

export type { AdminFeatureFlags, AdminRoleGrant, AdminUserRole }
export { DEFAULT_ADMIN_FEATURE_FLAGS, SUPREME_ADMIN_EMAIL, LOCAL_ADMINS_KEY, LOCAL_FLAGS_KEY, E2E_ADMIN_KEY }
export type { CommercePlan, CommerceTesterGrant, PreforgedOffer }
export { DEFAULT_COMMERCE_PLANS }
export type AdminAccessStatus = 'loading' | 'allowed' | 'denied'

export interface AdminSnapshot {
  status: AdminAccessStatus
  uid: string | null
  email: string | null
  role: AdminUserRole
  isAdmin: boolean
  isSupremeAdmin: boolean
  source: 'backend' | 'local'
  flags: AdminFeatureFlags
  roleGrants: AdminRoleGrant[]
  commercePlans: CommercePlan[]
  testerGrants: CommerceTesterGrant[]
  preforgedOffers: PreforgedOffer[]
  error: string
}

const subscribers = new Set<() => void>()
let refreshPromise: Promise<AdminSnapshot> | null = null
let snapshotValue: AdminSnapshot = {
  status: 'loading',
  uid: null,
  email: null,
  role: 'normal',
  isAdmin: false,
  isSupremeAdmin: false,
  source: 'local',
  flags: { ...DEFAULT_ADMIN_FEATURE_FLAGS },
  roleGrants: [],
  commercePlans: [...DEFAULT_COMMERCE_PLANS],
  testerGrants: [],
  preforgedOffers: [],
  error: '',
}

function emit(): void {
  for (const fn of subscribers) fn()
}

function mergeVisibleRoleGrants(roleGrants: AdminRoleGrant[], legacyAdminUids: string[]): AdminRoleGrant[] {
  const merged = normalizeRoleGrantList([
    ...buildLegacyAdminRoleGrants(legacyAdminUids),
    ...roleGrants,
    { email: SUPREME_ADMIN_EMAIL, displayName: 'Dharmax', role: 'supreme' },
  ])
  return merged
}

function buildSnapshotFromLocal(user: { uid: string | null; email: string | null }, error = ''): AdminSnapshot {
  const flags = normalizeFeatureFlags(loadLocalFlags())
  const localLegacyAdmins = loadLocalLegacyAdmins()
  const localRoleGrants = loadLocalRoleGrants()
  const commercePlans = readLocalCommercePlans()
  const testerGrants = readLocalCommerceTesterGrants()
  const preforgedOffers = readLocalPreforgedOffers()
  const roleGrants = mergeVisibleRoleGrants(localRoleGrants, localLegacyAdmins)
  const role = user.uid || user.email
    ? resolveAssignedRole(user, roleGrants, {
        legacyAdmin: !!user.uid && localLegacyAdmins.includes(user.uid),
        e2eAdmin: hasE2EAdminOverride(),
      })
    : 'normal'
  const isAdmin = isAdminRole(role)
  writeAdminRuntimeFlags(flags)
  return {
    status: user.uid || user.email ? (isAdmin ? 'allowed' : 'denied') : 'denied',
    uid: user.uid,
    email: user.email,
    role,
    isAdmin,
    isSupremeAdmin: role === 'supreme',
    source: 'local',
    flags,
    roleGrants,
    commercePlans,
    testerGrants,
    preforgedOffers,
    error,
  }
}

function loadLocalFlags(): AdminFeatureFlags {
  try {
    const raw = localStorage.getItem(LOCAL_FLAGS_KEY)
    return normalizeFeatureFlags(raw ? JSON.parse(raw) : {})
  } catch {
    return { ...DEFAULT_ADMIN_FEATURE_FLAGS }
  }
}

function saveLocalFlags(flags: AdminFeatureFlags): void {
  writeAdminRuntimeFlags(flags)
}

async function refreshSnapshot(): Promise<AdminSnapshot> {
  const user = authStore.user
  const uid = user?.uid ?? null
  const email = user?.email?.trim().toLowerCase() ?? null
  const localLegacyAdmins = loadLocalLegacyAdmins()
  const localRoleGrants = loadLocalRoleGrants()

  if (!uid && !email) {
    snapshotValue = buildSnapshotFromLocal({ uid, email })
    emit()
    return snapshotValue
  }

  try {
    const configMap = await loadAdminConfig(['admins', 'flags', 'adminRoles', 'commerceCatalog', 'testerAccess', 'preforgedCatalog'])
    const adminData = configMap['admins'] as { uids?: unknown[] } | undefined
    const remoteFlags = configMap['flags'] as Record<string, unknown> | undefined
    const roleData = configMap['adminRoles'] as { grants?: AdminRoleGrant[] } | undefined
    const commerceCatalog = configMap['commerceCatalog'] as { plans?: CommercePlan[] } | undefined
    const testerAccess = configMap['testerAccess'] as { grants?: CommerceTesterGrant[] } | undefined
    const preforgedCatalog = configMap['preforgedCatalog'] as { offers?: PreforgedOffer[] } | undefined
    const remotePlans = commerceCatalog
      ? normalizeCommercePlans(commerceCatalog.plans)
      : readLocalCommercePlans()
    const remoteTesterGrants = testerAccess
      ? normalizeCommerceTesterGrants(testerAccess.grants)
      : readLocalCommerceTesterGrants()
    const remotePreforgedOffers = preforgedCatalog
      ? normalizePreforgedOffers(preforgedCatalog.offers)
      : readLocalPreforgedOffers()
    
    const remoteLegacyAdminUids = Array.isArray(adminData?.uids)
      ? adminData.uids.map((entry: unknown) => String(entry ?? '').trim()).filter(Boolean)
      : []
    const legacyAdminUids = [...new Set([...remoteLegacyAdminUids, ...localLegacyAdmins])]
    const roleGrants = mergeVisibleRoleGrants([
      ...normalizeRoleGrantList(roleData?.grants),
      ...localRoleGrants,
    ], legacyAdminUids)
    
    const role = resolveAssignedRole({ uid, email }, roleGrants, {
      legacyAdmin: !!uid && legacyAdminUids.includes(uid),
      e2eAdmin: hasE2EAdminOverride(),
    })
    const flags = normalizeFeatureFlags(remoteFlags)
    writeAdminRuntimeFlags(flags)
    
    snapshotValue = {
      status: isAdminRole(role) ? 'allowed' : 'denied',
      uid,
      email,
      role,
      isAdmin: isAdminRole(role),
      isSupremeAdmin: role === 'supreme',
      source: 'backend',
      flags,
      roleGrants,
      commercePlans: remotePlans,
      testerGrants: remoteTesterGrants,
      preforgedOffers: remotePreforgedOffers,
      error: '',
    }
    emit()
    return snapshotValue
  } catch (error) {
    snapshotValue = buildSnapshotFromLocal({ uid, email }, `Supabase admin config unavailable: ${String(error).slice(0, 160)}`)
    emit()
    return snapshotValue
  }
}

function queueRefresh(): Promise<AdminSnapshot> {
  if (!refreshPromise) {
    snapshotValue = { ...snapshotValue, status: authStore.user ? 'loading' : snapshotValue.status }
    emit()
    refreshPromise = refreshSnapshot().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

authStore.subscribe(() => {
  void queueRefresh()
})
void queueRefresh()

async function persistRoleGrants(grants: AdminRoleGrant[]): Promise<void> {
  const normalized = normalizeRoleGrantList(grants.filter(grant => grant.role !== 'supreme'))
  if (snapshotValue.source === 'backend') {
    await saveAdminConfig('adminRoles', { grants: normalized, updatedAtMs: Date.now() })
  } else {
    saveLocalRoleGrants(normalized)
  }
}

export const adminStore = {
  get snapshot(): AdminSnapshot {
    return snapshotValue
  },

  get status(): AdminAccessStatus {
    return snapshotValue.status
  },

  get isAdmin(): boolean {
    return snapshotValue.isAdmin
  },

  get isSupremeAdmin(): boolean {
    return snapshotValue.isSupremeAdmin
  },

  get role(): AdminUserRole {
    return snapshotValue.role
  },

  get flags(): AdminFeatureFlags {
    return snapshotValue.flags
  },

  get roleGrants(): AdminRoleGrant[] {
    return snapshotValue.roleGrants
  },

  get commercePlans(): CommercePlan[] {
    return snapshotValue.commercePlans
  },

  get testerGrants(): CommerceTesterGrant[] {
    return snapshotValue.testerGrants
  },

  get preforgedOffers(): PreforgedOffer[] {
    return snapshotValue.preforgedOffers
  },

  subscribe(fn: () => void): () => void {
    subscribers.add(fn)
    return () => subscribers.delete(fn)
  },

  refresh(): Promise<AdminSnapshot> {
    return queueRefresh()
  },

  async saveFlags(flags: AdminFeatureFlags): Promise<void> {
    const next = normalizeFeatureFlags(flags)
    if (!snapshotValue.isAdmin) throw new Error('Admin access required to save feature flags.')
    if (snapshotValue.source === 'backend') {
      await saveAdminConfig('flags', { ...next, updatedAtMs: Date.now() })
    } else {
      saveLocalFlags(next)
    }
    writeAdminRuntimeFlags(next)
    snapshotValue = {
      ...snapshotValue,
      flags: {
        ...next,
        updatedAtMs: Date.now(),
      },
      error: '',
    }
    emit()
  },

  async saveRoleGrant(grant: AdminRoleGrant): Promise<void> {
    if (!snapshotValue.isSupremeAdmin) throw new Error('Supreme admin access required to manage roles.')
    const key = roleGrantKey(grant)
    if (!key) throw new Error('Role grants need at least a UID or email.')
    const stamped: AdminRoleGrant = {
      ...grant,
      updatedAtMs: Date.now(),
      updatedByUid: snapshotValue.uid ?? undefined,
      updatedByEmail: snapshotValue.email ?? undefined,
    }
    const current = normalizeRoleGrantList(snapshotValue.roleGrants.filter(entry => entry.role !== 'supreme'))
      .filter(entry => roleGrantKey(entry) !== key)
    const next = stamped.role === 'normal' ? current : [...current, stamped]
    await persistRoleGrants(next)
    await queueRefresh()
  },

  async saveCommercePlan(plan: CommercePlan): Promise<void> {
    if (!snapshotValue.isAdmin) throw new Error('Admin access required to manage commerce catalog.')
    const normalized = normalizeCommercePlan(plan)
    const next = normalizeCommercePlans([
      ...snapshotValue.commercePlans.filter(entry => entry.id !== normalized.id),
      normalized,
    ])
    if (snapshotValue.source === 'backend') {
      await saveAdminConfig('commerceCatalog', { plans: next, updatedAtMs: Date.now() })
    } else {
      saveLocalCommercePlans(next)
    }
    snapshotValue = {
      ...snapshotValue,
      commercePlans: next,
      error: '',
    }
    emit()
  },

  async saveTesterGrant(grant: CommerceTesterGrant): Promise<void> {
    if (!snapshotValue.isAdmin) throw new Error('Admin access required to manage tester grants.')
    const normalized = normalizeCommerceTesterGrant({
      ...grant,
      updatedAtMs: Date.now(),
      updatedByUid: snapshotValue.uid ?? undefined,
      updatedByEmail: snapshotValue.email ?? undefined,
    })
    if (!normalized) throw new Error('Tester grants need an identity and a valid target.')
    const key = testerGrantKey(normalized)
    const next = normalizeCommerceTesterGrants([
      ...snapshotValue.testerGrants.filter(entry => {
        const entryKey = testerGrantKey(entry)
        return entryKey !== key
      }),
      normalized,
    ])
    if (snapshotValue.source === 'backend') {
      await saveAdminConfig('testerAccess', { grants: next, updatedAtMs: Date.now() })
    } else {
      saveLocalCommerceTesterGrants(next)
    }
    snapshotValue = {
      ...snapshotValue,
      testerGrants: next,
      error: '',
    }
    emit()
  },

  async savePreforgedOffer(offer: PreforgedOffer): Promise<void> {
    if (!snapshotValue.isAdmin) throw new Error('Admin access required to manage preforged offers.')
    const normalized = normalizePreforgedOffer({
      ...offer,
      updatedAtMs: Date.now(),
      updatedByUid: snapshotValue.uid ?? undefined,
      updatedByEmail: snapshotValue.email ?? undefined,
    })
    if (!normalized) throw new Error('Preforged offers need a stable id.')
    const next = normalizePreforgedOffers([
      ...snapshotValue.preforgedOffers.filter(entry => entry.id !== normalized.id),
      normalized,
    ])
    if (snapshotValue.source === 'backend') {
      await saveAdminConfig('preforgedCatalog', { offers: next, updatedAtMs: Date.now() })
    } else {
      saveLocalPreforgedOffers(next)
    }
    snapshotValue = {
      ...snapshotValue,
      preforgedOffers: next,
      error: '',
    }
    emit()
  },

  async deletePreforgedOffer(offerId: string): Promise<void> {
    if (!snapshotValue.isAdmin) throw new Error('Admin access required.')
    const next = snapshotValue.preforgedOffers.filter(entry => entry.id !== offerId)
    if (snapshotValue.source === 'backend') {
      await saveAdminConfig('preforgedCatalog', { offers: next, updatedAtMs: Date.now() })
    } else {
      saveLocalPreforgedOffers(next)
    }
    snapshotValue = {
      ...snapshotValue,
      preforgedOffers: next,
      error: '',
    }
    emit()
  },

  async deleteTesterGrant(grant: CommerceTesterGrant): Promise<void> {
    if (!snapshotValue.isAdmin) throw new Error('Admin access required.')
    const key = testerGrantKey(grant)
    const next = snapshotValue.testerGrants.filter(entry => testerGrantKey(entry) !== key)
    if (snapshotValue.source === 'backend') {
      await saveAdminConfig('testerAccess', { grants: next, updatedAtMs: Date.now() })
    } else {
      saveLocalCommerceTesterGrants(next)
    }
    snapshotValue = {
      ...snapshotValue,
      testerGrants: next,
      error: '',
    }
    emit()
  },
}
